<script setup lang="ts">
import { ref } from "vue";
import { useApiClient } from "@/api/client";
import OpportunityDO = Modules.CampaignPlanner.Data.OpportunityDO;
import { toastApiResult } from "@/toaster";

const props = defineProps<{
	opportunity: OpportunityDO;
}>();

defineEmits<{
	addFlight: [];
}>();

const syncing = ref(false);

const syncToBroadsign = async () => {
	syncing.value = true;
	await toastApiResult(
		useApiClient("campaignPlanner").syncOpportunityToBroadsign(
			props.opportunity.uuid
		)
	);
	syncing.value = false;
};
</script>

<template>
	<v-card class="opportunity-header mt-5 mb-3">
		<div class="opportunity-header__inner">
			<div class="opportunity-header__head">
				<h2 class="text-h6 font-weight-bold opportunity-header__title">
					{{ opportunity.name }}
				</h2>

				<div class="opportunity-header__actions">
					<v-btn
						variant="tonal"
						prepend-icon="fa: fa-regular fa-rotate"
						size="small"
						:loading="syncing"
						@click="syncToBroadsign"
					>
						Sync to Broadsign
					</v-btn>
					<v-btn
						color="primary"
						prepend-icon="fa: fa-regular fa-plus"
						size="small"
						@click="$emit('addFlight')"
					>
						Add Flight
					</v-btn>
				</div>
			</div>

			<div class="opportunity-header__meta">
				<div class="opportunity-header__meta-item">
					<span class="opportunity-header__label">Advertiser</span>
					<span class="opportunity-header__value">{{
						opportunity.advertiser_name
					}}</span>
				</div>
				<div class="opportunity-header__meta-item">
					<span class="opportunity-header__label">Account</span>
					<span class="opportunity-header__value">{{
						opportunity.account_name
					}}</span>
				</div>
				<div class="opportunity-header__meta-item">
					<span class="opportunity-header__label">Source</span>
					<span class="opportunity-header__value">{{
						opportunity.sales_source
					}}</span>
				</div>
				<div class="opportunity-header__meta-item">
					<span class="opportunity-header__label">Type</span>
					<span class="opportunity-header__value">{{
						opportunity.campaign_type
					}}</span>
				</div>
				<div v-if="opportunity.client_email" class="opportunity-header__meta-item">
					<span class="opportunity-header__label">Email</span>
					<span class="opportunity-header__value">{{
						opportunity.client_email
					}}</span>
				</div>
				<div
					v-if="opportunity.client_phonenumber"
					class="opportunity-header__meta-item"
				>
					<span class="opportunity-header__label">Phone</span>
					<span class="opportunity-header__value">{{
						opportunity.client_phonenumber
					}}</span>
				</div>
			</div>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.opportunity-header {
	// Inherits the app's elevated v-card shadow, border-radius and surface.
	// The accent rail is a pseudo-element so it doesn't fight the v-card's
	// internal flex/overlay layers.
	position: relative;
	overflow: hidden;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		width: 5px;
		background: linear-gradient(
			rgb(var(--v-theme-info)),
			rgba(var(--v-theme-info), 0.65)
		);
		z-index: 1;
	}

	&__inner {
		min-width: 0;
		padding: 20px 24px;
	}

	&__head {
		display: flex;
		align-items: flex-start;
		gap: 16px;
	}

	&__title {
		min-width: 0;
		letter-spacing: -0.015em;
		line-height: 1.3;
	}

	&__actions {
		margin-left: auto;
		display: flex;
		gap: 8px;
		flex: 0 0 auto;
	}

	&__meta {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 6px 22px;
		margin-top: 14px;
		padding-top: 14px;
		border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	}

	&__meta-item {
		display: flex;
		align-items: baseline;
		gap: 7px;
		min-width: 0;
	}

	&__label {
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: rgba(var(--v-theme-on-surface), 0.55);
	}

	&__value {
		font-size: 14px;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

@media (max-width: 720px) {
	.opportunity-header__head {
		flex-direction: column;
	}

	.opportunity-header__actions {
		margin-left: 0;
	}
}
</style>
